import { autoSaveNotifier } from '../../../webmodule-common/other/context/autoSaveNotifier.js';
import { FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker.js';
import {
  fireQuickErrorToast,
  fireQuickInformationToast,
  fireQuickSuccessToast
} from '../../../webmodule-common/other/toast-away.js';
import { getConfirmationReasonFor } from '../../../webmodule-common/other/ui/modal-confirmation-reason.js';
import { information } from '../../../webmodule-common/other/ui/modal-option.js';
import { InformationDispatcher } from '../../../webmodule-common/other/ui/information-dispatcher.js';
import { QuoteTechnicalReviewDialog } from '../data/quote-technical-review-confirmation.js';
import { saveWithIndicator, SaveWorkflowModal } from '../../../webmodule-common/other/save-workflow.js';
import { showDynamicInputModal } from '../../../webmodule-common/other/ui/modal-input.js';
import {
  showValidations,
  showValidationsWithCustomOptions
} from '../../../webmodule-common/other/ui/modal-validationhandler.js';
import { waitPatientlySavingWithLock } from '../../../webmodule-common/other/ui/modal-saving.js';

import type {
  QuoteProgressSession,
  QuoteReasonRequest,
  QuoteReasonResponse,
  QuoteTechnicalReviewRequest,
  QuoteTechnicalReviewResponse,
  QuoteTextPromptRequest,
  QuoteUserInteraction,
  QuoteValidationButton
} from '../data/quote-user-interaction.js';

/**
 * The real, Lit-backed {@link QuoteUserInteraction}. Everything the quote managers used to construct
 * inline lives here instead, so the data layer no longer imports modals, dialogs or toasts - see
 * quote-user-interaction.ts for why that mattered.
 */
export class UiQuoteUserInteraction implements QuoteUserInteraction {
  notifySuccess(message: string, durationMs?: number): void {
    fireQuickSuccessToast(message, durationMs);
  }

  notifyError(message: string, durationMs?: number): void {
    fireQuickErrorToast(message, durationMs);
  }

  notifyInformation(message: string, durationMs?: number): void {
    fireQuickInformationToast(message, durationMs);
  }

  notifyAutoSaveSuccess(): void {
    autoSaveNotifier.triggerAutoSaveSuccess();
  }

  async inform(message: string, title?: string): Promise<void> {
    await information(message, title);
  }

  async showValidations(messages: string[], header?: string): Promise<void> {
    // The modal layer takes a lazy snippet; the callers hold a resolved string by this point.
    await showValidations(messages, header === undefined ? undefined : () => header);
  }

  showValidationsWithOptions(
    messages: string[],
    header: string,
    buttons: QuoteValidationButton[]
  ): Promise<'discard' | 'stay'> {
    return showValidationsWithCustomOptions(messages, () => header, buttons) as Promise<'discard' | 'stay'>;
  }

  async promptForText(request: QuoteTextPromptRequest): Promise<string | null> {
    const result = await showDynamicInputModal(
      () => request.title,
      [
        {
          label: request.label,
          value: request.value,
          maxLength: request.maxLength,
          required: request.required,
          valueType: FieldType.string
        }
      ]
    );
    if (!result.confirmed) return null;
    return result.fields[0].value as string;
  }

  async confirmWithReason(request: QuoteReasonRequest): Promise<QuoteReasonResponse | null> {
    const result = await getConfirmationReasonFor(
      request.options,
      request.title,
      request.message,
      request.selectReasonLabel,
      request.confirmButtonText
    );
    if (!result.result || !result.reason) return null;

    return { reasonId: result.reason.id, comment: result.comment ?? '' };
  }

  async confirmTechnicalReview(request: QuoteTechnicalReviewRequest): Promise<QuoteTechnicalReviewResponse | null> {
    const dialog = new QuoteTechnicalReviewDialog(
      request.poNumberRequired,
      request.forceIssueOrder,
      request.supplierName,
      request.currentOrderNumber
    );
    await dialog.showModal();
    if (!dialog.ok) return null;

    return {
      allowSupplierToIssue: dialog.allowSupplierToIssue,
      orderNumber: dialog.orderNumber
    };
  }

  async beginProgress(title: string, steps: number, initialInformation?: string): Promise<QuoteProgressSession> {
    const dispatcher = new InformationDispatcher();
    // Set before the modal opens so it renders with content already in place, matching the order
    // the callers relied on when they built this workflow themselves.
    if (initialInformation !== undefined) await dispatcher.setInformation(initialInformation);

    const modal = new SaveWorkflowModal(title, dispatcher, steps);
    // Deliberately not awaited to completion - the modal stays up while the caller works.
    const modalState = await modal.show();
    await modalState.onShow;

    return {
      setInformation: (markdown: string) => dispatcher.setInformation(markdown),
      close: async () => {
        modal.done();
        await modal.hideModal();
      }
    };
  }

  runWithSavingLock<T>(action: Promise<T>): Promise<T | null> {
    return waitPatientlySavingWithLock<T>({ action });
  }

  saveWithIndicator(save: () => Promise<boolean>): Promise<boolean> {
    return saveWithIndicator(save);
  }
}
